"use client";

import { ChartBar as BarChart3, Mic, NotebookPen, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: rootRef,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const panels = [{
    id: 1,
    rotation: "-rotate-3",
    offset: "md:-translate-x-6 md:translate-y-2",
    icon: NotebookPen,
    eyebrow: "Today, 8:42 AM",
    title: "A clearer vision",
    body: "Strategy session complete. The path forward feels focused and achievable.",
    tint: "from-[hsl(43_46%_55%/0.35)] to-[hsl(43_46%_55%/0.25)]"
  }, {
    id: 2,
    rotation: "rotate-1",
    offset: "md:translate-y-0",
    icon: BarChart3,
    eyebrow: "Growth metrics - 7 days",
    title: "Steady progress",
    body: "",
    tint: "from-[hsl(43_46%_55%/0.35)] to-[hsl(222_47%_7%/0.25)]"
  }, {
    id: 3,
    rotation: "rotate-[4deg]",
    offset: "md:translate-x-6 md:translate-y-3",
    icon: Mic,
    eyebrow: "Client brief - 0:38",
    title: "Project alignment",
    body: "",
    tint: "from-[hsl(43_46%_55%/0.4)] to-[hsl(222_47%_7%/0.18)]"
  }];
  const trendBars = [38, 46, 52, 49, 58, 64, 71];
  return <section data-section-id="13758" id="hero" className="relative overflow-hidden bg-background text-foreground py-28 md:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" style={{
      background: "radial-gradient(60% 50% at 18% 22%, hsl(43 46% 55% / 0.25), transparent 60%), radial-gradient(55% 45% at 12% 38%, hsl(43 46% 55% / 0.20), transparent 65%), radial-gradient(50% 50% at 88% 78%, hsl(222 47% 7% / 0.15), transparent 60%), radial-gradient(45% 40% at 75% 88%, hsl(222 47% 7% / 0.10), transparent 60%), radial-gradient(40% 35% at 50% 50%, hsl(43 46% 55% / 0.15), transparent 70%)",
      backgroundSize: "180% 180%",
      animation: "auroraDrift 24s ease-in-out infinite alternate"
    }} />

      <style>{`
        @keyframes auroraDrift {
          0% { background-position: 0% 0%; }
          100% { background-position: 18% 12%; }
        }
        @keyframes coralBreath {
          0%, 100% { opacity: 0.18; transform: scale(1); }
          50% { opacity: 0.32; transform: scale(1.05); }
        }
        @keyframes crispIn {
          0% { filter: blur(8px); opacity: 0; transform: translateY(24px); }
          100% { filter: blur(0); opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div ref={rootRef} className="relative mx-auto max-w-[1240px] px-6 md:px-8">
        <div className="mx-auto flex max-w-[880px] flex-col items-center text-center">
          {}
          <div className={`transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge variant="outline" className="border-border bg-card backdrop-blur-xl text-muted-foreground px-4 py-1.5 text-[0.8125rem] font-medium tracking-[0.04em] rounded-lg gap-1.5">
              <Sparkles className="size-3.5 text-secondary" strokeWidth={1.5} />
              <span>Professional business consulting</span>
            </Badge>
          </div>

          {}
          <h1 className={`font-default mt-8 text-foreground`} style={{
          fontSize: "clamp(2.75rem, 6.2vw, 5.5rem)",
          fontWeight: 700,
          letterSpacing: "-0.025em",
          lineHeight: 1.02,
          animation: isVisible ? "crispIn 900ms cubic-bezier(0.22, 1, 0.36, 1) forwards" : "none",
          opacity: isVisible ? 1 : 0
        }}>
            Define your{" "}
            <em className="not-italic text-secondary" style={{
            fontStyle: "italic",
            fontWeight: 400
          }}>
              success
            </em>
            .
          </h1>

          {}
          <p className={`mt-7 max-w-[560px] text-muted-foreground transition-all duration-700 ease-out delay-200 font-default ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
          fontSize: "clamp(1.0625rem, 1.3vw, 1.25rem)",
          lineHeight: 1.6
        }}>
            We transform complex business challenges into clear, actionable strategies for sustainable growth.
          </p>

          {}
          <div className={`mt-10 transition-all duration-700 ease-out delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative inline-block">
              <div aria-hidden="true" className="absolute inset-0 -z-10 rounded-lg bg-secondary blur-2xl" style={{
              animation: "coralBreath 3.6s ease-in-out infinite"
            }} />
              <Button asChild size="lg" className="h-12 rounded-lg px-7 bg-secondary text-primary-foreground hover:bg-secondary/90 font-medium shadow-lg transition-transform hover:-translate-y-0.5">
                <Link to="#">View our services</Link>
              </Button>
            </div>
          </div>

          {}
          <div className={`mt-20 w-full transition-all duration-1000 ease-out delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
            <div className="grid grid-cols-1 gap-6 md:flex md:items-end md:justify-center md:gap-0">
              {panels.map((panel, i) => {
              const Icon = panel.icon;
              return <div key={panel.id} data-index={i} className={`group relative md:w-[280px] md:mx-[-12px] ${panel.rotation} ${panel.offset} transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:rotate-0 hover:-translate-y-2 hover:z-10`} style={{
                transformStyle: "preserve-3d"
              }}>
                    {}
                    <div aria-hidden="true" className={`absolute -inset-4 -z-10 rounded-lg bg-gradient-to-br ${panel.tint} blur-2xl opacity-70`} />
                    <div className="relative rounded-lg p-6 backdrop-blur-2xl border border-border bg-card shadow-lg" style={{
                  boxShadow: "0 20px 60px -20px hsl(222 47% 7% / 0.15)"
                }}>
                      <div className="flex items-center gap-3">
                        <div className="relative flex size-9 items-center justify-center rounded-lg">
                          <div className="absolute inset-0 rounded-lg bg-secondary/20" />
                          <Icon className="relative size-[18px] text-secondary" strokeWidth={1.5} />
                        </div>
                        <span className="text-[0.75rem] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                          {panel.eyebrow}
                        </span>
                      </div>

                      <h3 className="mt-4 font-default text-card-foreground" style={{
                    fontSize: "1.0625rem",
                    fontWeight: 600,
                    letterSpacing: "-0.015em",
                    lineHeight: 1.25
                  }}>
                        {panel.title}
                      </h3>

                      {panel.id === 1 && <p className="mt-2 text-[0.875rem] leading-[1.55] text-muted-foreground font-default">
                          {panel.body}
                        </p>}

                      {panel.id === 2 && <div className="mt-4 flex items-end justify-between gap-1.5 h-16">
                          {trendBars.map((h, idx) => <div key={idx} data-index={idx} className="flex-1 rounded-md bg-secondary" style={{
                      height: `${h}%`
                    }} />)}
                        </div>}

                      {panel.id === 3 && <div className="mt-4 flex items-center gap-[3px] h-10">
                          {Array.from({
                      length: 28
                    }).map((_, idx) => {
                      const heights = [30, 55, 75, 45, 90, 60, 35, 70, 85, 50, 25, 65, 80, 95, 55, 40, 70, 60, 85, 45, 30, 75, 55, 90, 65, 40, 50, 35];
                      return <div key={idx} data-index={idx} className="flex-1 rounded-lg bg-secondary/70" style={{
                        height: `${heights[idx]}%`
                      }} />;
                    })}
                        </div>}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}